import { useEffect, useImperativeHandle } from 'react';
import styled from 'styled-components';

import ExternalAttachment from 'components/shared/ExternalAttachment';
import FileDropZone from 'components/shared/FileDropZone';
import Upload from 'hooks/useDirectUpload/Upload';
import useDirectUpload from 'hooks/useDirectUpload/useDirectUpload';
import colors from 'styles/theme/colors';
import { Label } from 'styles/typography';
import ClipIcon from 'svg/AttachFileIcon';

import InternalAttachment from '../InternalAttachment';

export type FileInputHandle = {
  reset: () => void;
};

type FileInputComponentProps = {
  ref?: React.Ref<FileInputHandle>;
  allowedTypes?: { [key: string]: string[] };
  onChange?: (uploads: Upload[]) => void;
  onUploading?: (uploading: boolean) => void;
  renderUploadsInternally?: boolean;
  onDeleteUpload?: (id: string) => void;
  onSetDocType?: (id: string, docType: string | null) => void;
  defaultDocType?: string;
  initialUploads?: Upload[];
  draggingStyles?: React.CSSProperties;
  containerStyle?: React.CSSProperties;
  hideLabel?: boolean;
  Icon?: React.ElementType<React.SVGProps<SVGSVGElement>>;
};

export default function FileInputComponent(props: FileInputComponentProps) {
  const {
    ref,
    allowedTypes,
    onChange,
    onUploading,
    renderUploadsInternally = false,
    onDeleteUpload,
    onSetDocType,
    defaultDocType,
    initialUploads = [],
    draggingStyles = {
      backgroundColor: 'var(--primary-blue-05)',
      borderColor: 'var(--primary-blue)',
    },
    containerStyle,
    hideLabel = false,
    Icon = () => <ClipIcon height={16} />,
  } = props;

  const { uploadFiles, uploads, uploading, removeUpload, setDocType, resetUploads } = useDirectUpload(
    [...initialUploads],
    {
      defaultDocType,
    }
  );

  useImperativeHandle(ref, () => ({ reset: resetUploads }), [resetUploads]);

  useEffect(() => {
    onChange?.(uploads);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploads]);

  useEffect(() => {
    onUploading?.(uploading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploading]);

  const handleDelete = (id: string) => {
    removeUpload(id);
    onDeleteUpload?.(id);
  };

  const handleSetDocType = (id: string, docType: string | null) => {
    setDocType(id, docType);
    onSetDocType?.(id, docType);
  };

  const canAcceptMoreFiles = uploads.length < 10;

  return (
    <>
      <FileDropZone
        onFilesSelected={uploadFiles}
        noClick
        noDrag={false}
        currentFileCount={uploads.length}
        allowedTypes={allowedTypes}>
        {(dropzone) => (
          <Container
            {...dropzone.getRootProps()}
            onClick={canAcceptMoreFiles ? dropzone.open : () => {}}
            $active={canAcceptMoreFiles && dropzone.isDragActive}
            $canAcceptMoreFiles={canAcceptMoreFiles}
            $draggingStyles={draggingStyles}
            style={containerStyle}>
            <input {...dropzone.getInputProps()} />
            {renderUploadsInternally && uploads.length > 0 ? (
              <FilesContainer>
                {uploads.map((upload) => (
                  <InternalAttachment key={upload.id} upload={upload} handleDelete={handleDelete} />
                ))}
              </FilesContainer>
            ) : (
              <EmptyStateContainer>
                <Icon />
                {!hideLabel && (
                  <InputLabel>
                    Drag and drop your file(s) here, or&nbsp;
                    <FileBrowserLink aria-label='launch file browser'>browse your files</FileBrowserLink>
                  </InputLabel>
                )}
              </EmptyStateContainer>
            )}

            {renderUploadsInternally && !canAcceptMoreFiles && (
              <LimitContainer>
                <LimitMessage>A maximum of 10 files can be provided</LimitMessage>
              </LimitContainer>
            )}
          </Container>
        )}
      </FileDropZone>
      {!renderUploadsInternally && (
        <AttachmentContainer>
          <>
            {uploads.map((upload) => (
              <ExternalAttachment
                key={upload.id}
                error={upload.hasError}
                attachment={upload}
                uploadProgress={upload.progress}
                onDeleteAttachment={handleDelete}
                onDocumentTypeChange={handleSetDocType}
              />
            ))}
          </>
        </AttachmentContainer>
      )}
    </>
  );
}

const Container = styled.div<{ $active: boolean; $canAcceptMoreFiles: boolean; $draggingStyles: React.CSSProperties }>`
  background-color: ${({ $active, $draggingStyles }) => ($active ? $draggingStyles.backgroundColor : 'white')};
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  border-width: 1px;
  border-style: dashed;
  border-color: ${({ $active, $draggingStyles }) => ($active ? $draggingStyles.borderColor : 'var(--black-25)')};
  position: relative;
  display: flex;
  min-height: 120px;
  flex-direction: column;

  ${({ $canAcceptMoreFiles, $draggingStyles }) =>
    $canAcceptMoreFiles &&
    `
    cursor: pointer;

    &:hover {
      background-color: ${$draggingStyles.backgroundColor};
      border-color: ${$draggingStyles.borderColor};
    }
  `}

  &:focus-visible {
    outline: none;
  }
`;

const AttachmentContainer = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  flex: 1;
  padding: 0 16px;
`;

const FileBrowserLink = styled.a`
  color: var(--primary-blue);
  cursor: pointer;
  text-decoration: underline;
`;

const InputLabel = styled(Label)`
  margin-top: 8px;
`;

const FilesContainer = styled.div`
  display: flex;
  gap: 4px;
  width: 100%;
  height: 100%;
  justify-content: flex-start;
  align-items: flex-start;
  align-content: flex-start;
  flex-wrap: wrap;
  padding: 16px;
`;

const LimitContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding-bottom: 16px;
`;

const LimitMessage = styled.p`
  font-size: 12px;
  font-weight: normal;
  color: ${colors.black};
  font-style: italic;
`;
